<template>
  <transition name="fade">
    <div
      :class="['modal-backdrop', 'backdrop', size]"
      v-show="open"
      @click.self="closeBackdrop"
    >
      <transition :name="size === 'side' ? 'slide-from-right' : 'fade'">
        <div
          class="modal"
          :class="[size]"
          role="dialog"
          v-show="open"
        >
          <div class="modal-content">
            <header v-if="!hideHeader" class="modal-header">
              <div class="container">
                <align justify="space-between">
                  <h4 class="secondary-font">{{ title }}</h4>

                  <icon-button
                    class="modal-close"
                    icon="close"
                    @click="close"
                  />
                </align>
              </div>
            </header>

            <section class="modal-body">
              <div class="container">
                <slot />
              </div>
            </section>
          </div>

          <section v-if="$slots.footer" class="modal-footer">
            <div class="container">
              <slot name="footer" />
            </div>
          </section>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
import Align from './Align';
import IconButton from './IconButton';

export default {
  components: {
    Align,
    IconButton,
  },

  props: {
    title: String,
    open: Boolean,
    disableCloseBackdrop: Boolean,
    hideHeader: Boolean,
    size: {
      type: String,
      options: ['default', 'large', 'full', 'side'],
      default: 'default',
    },
  },

  mounted() {
    this.$bus.$on('keyboard-shortcut-esc', this.handleKeyboardEscape);
  },

  beforeDestroy() {
    this.$bus.$off('keyboard-shortcut-esc', this.handleKeyboardEscape);
  },

  watch: {
    open(value) {
      if (value) {
        document.body.classList.add('disable-scroll');
      } else {
        document.body.classList.remove('disable-scroll');
      }
    },
  },

  methods: {
    handleKeyboardEscape() {
      if (this.open) {
        this.close();
      }
    },

    close() {
      document.body.classList.remove('disable-scroll');
      this.$emit('close');
    },

    closeBackdrop() {
      if (!this.disableCloseBackdrop) {
        this.close();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../scss/variables.scss';

.modal-backdrop {
  @media($medium-down) {
    padding: 0;
  }

  &.full,
  &.side {
    padding: 0;
  }
}

.modal {
  background: var(--theme-background);
  box-shadow: $elevation-1;
  display: flex;
  flex-direction: column;
  border-radius: $border-radius;
  max-width: 600px;
  max-height: 100%;
  margin: $gp * 2 auto $gp * 6;

  @media($medium-down) {
    max-width: 100%;
    height: 100%;
    border-radius: 0;
    margin: 0;
  }

  &.large {
    max-width: 900px;
  }

  &.full {
    max-width: 100%;
    min-height: 100%;
    border-radius: 0;
    margin: 0;

    .modal-content {
      @include overflowY;
      flex: 1;
    }
  }

  &.side {
    min-height: 100%;
    border-radius: 0;
    margin: 0 0 0 auto;

    .modal-content {
      @include overflowY;
      flex: 1;
    }
  }
}

.modal-header {
  display: flex;
  padding: $gp $gp 0 $gp;

  h4 {
    @include ellipsis;
    flex: 1;
    margin-bottom: 0;
  }
}

.modal-body {
  padding: $gp;
}

.modal-footer {
  width: 100%;
  border-top: solid 1px var(--theme-border);
  padding: $gp;
  background-color: var(--theme-background);
}

.container {
  width: 100%;
  max-width: 1400px;
  margin: auto;
}
</style>
